import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import RichTextEditor from '../../components/admin/RichTextEditor';
import { 
  Save, 
  Loader2,
  Home,
  Image,
  Type,
  Plus,
  Trash2,
  GripVertical,
  ChevronDown,
  ChevronUp,
  Info,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const languages = [
  { code: 'ca', name: 'Català', flag: '🇦🇩' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
];

export const AdminHomeContent = () => {
  const navigate = useNavigate();
  const { isAdmin, authFetch } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('texts');
  const [activeLang, setActiveLang] = useState('ca');
  
  // Homepage content state
  const [homeContent, setHomeContent] = useState({
    welcome_pretitle: {},
    welcome_title: {},
    welcome_subtitle: {},
    welcome_image: '',
    welcome_cta_link: '',
    webcam_pretitle: {},
    webcam_title: {},
    webcam_subtitle: {},
    featured_pretitle: {},
    featured_title: {},
    location_pretitle: {},
    location_title: {},
    location_description: {}
  });
  
  // Featured sections state
  const [featuredSections, setFeaturedSections] = useState([]);
  const [expandedSections, setExpandedSections] = useState({});
  const [editedSections, setEditedSections] = useState({}); // Track local edits
  const [savingSection, setSavingSection] = useState(null); // Track which section is saving

  useEffect(() => {
    if (!isAdmin) {
      toast.error('Només els administradors poden accedir a aquesta pàgina');
      navigate('/admin/dashboard');
      return;
    }
    fetchContent();
  }, [isAdmin, navigate]);

  const fetchContent = async () => {
    try {
      const [homeRes, sectionsRes] = await Promise.all([
        fetch(`${API_URL}/api/homepage-content`, {  }),
        fetch(`${API_URL}/api/admin/featured-sections`, {  })
      ]);

      if (homeRes.ok) {
        const data = await homeRes.json();
        setHomeContent(prev => ({ ...prev, ...data }));
      }

      if (sectionsRes.ok) {
        const data = await sectionsRes.json();
        setFeaturedSections(data.sections || []);
        // Expand all sections by default
        const expanded = {};
        (data.sections || []).forEach(s => { expanded[s.id] = true; });
        setExpandedSections(expanded);
      }
    } catch (error) {
      console.error('Failed to fetch content:', error);
      toast.error('Error carregant contingut');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveHomeContent = async () => {
    setSaving(true);
    try {
      const response = await authFetch(`${API_URL}/api/admin/homepage-content`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify(homeContent)
      });

      if (response.ok) {
        toast.success('Contingut de la pàgina principal guardat');
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Error guardant contingut');
      }
    } catch (error) {
      toast.error('Error guardant contingut');
    } finally {
      setSaving(false);
    }
  };

  const handleInitContent = async () => {
    try {
      const response = await authFetch(`${API_URL}/api/admin/homepage-content/init`, {
        method: 'POST',
        
      });
      
      if (response.ok) {
        toast.success('Contingut inicialitzat');
        fetchContent();
      } else {
        const data = await response.json();
        toast.info(data.message || 'Contingut ja existent');
      }
    } catch (error) {
      toast.error('Error inicialitzant contingut');
    }
  };

  const handleInitSections = async () => {
    try {
      const response = await authFetch(`${API_URL}/api/admin/featured-sections/init`, {
        method: 'POST',
        
      });
      
      if (response.ok) {
        toast.success('Seccions inicialitzades');
        fetchContent();
      } else {
        const data = await response.json();
        toast.info(data.message || 'Seccions ja existents');
      }
    } catch (error) {
      toast.error('Error inicialitzant seccions');
    }
  };

  const updateTextField = (field, value) => {
    setHomeContent(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        [activeLang]: value
      }
    }));
  };

  const addFeaturedSection = async () => {
    try {
      const newSection = {
        title: { ca: 'Nova Secció', es: 'Nueva Sección', fr: 'Nouvelle Section', en: 'New Section' },
        description: { ca: '', es: '', fr: '', en: '' },
        image: '',
        link: ''
      };

      const response = await authFetch(`${API_URL}/api/admin/featured-sections`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify(newSection)
      });

      if (response.ok) {
        toast.success('Secció afegida');
        fetchContent();
      } else {
        toast.error('Error afegint secció');
      }
    } catch (error) {
      toast.error('Error afegint secció');
    }
  };

  const updateFeaturedSection = async (sectionId, updates) => {
    try {
      const response = await authFetch(`${API_URL}/api/admin/featured-sections/${sectionId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify(updates)
      });

      if (response.ok) {
        setFeaturedSections(prev => 
          prev.map(s => s.id === sectionId ? { ...s, ...updates } : s)
        );
        toast.success('Secció actualitzada');
      } else {
        toast.error('Error actualitzant secció');
      }
    } catch (error) {
      toast.error('Error actualitzant secció');
    }
  };

  const deleteFeaturedSection = async (sectionId) => {
    if (!window.confirm('Segur que vols eliminar aquesta secció?')) return;
    
    try {
      const response = await authFetch(`${API_URL}/api/admin/featured-sections/${sectionId}`, {
        method: 'DELETE',
        
      });

      if (response.ok) {
        setFeaturedSections(prev => prev.filter(s => s.id !== sectionId));
        toast.success('Secció eliminada');
      } else {
        toast.error('Error eliminant secció');
      }
    } catch (error) {
      toast.error('Error eliminant secció');
    }
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  // Update section locally (no API call)
  const updateSectionLocal = (sectionId, updates) => {
    setEditedSections(prev => ({
      ...prev,
      [sectionId]: {
        ...(prev[sectionId] || featuredSections.find(s => s.id === sectionId)),
        ...updates
      }
    }));
  };

  // Get current section data (edited or original)
  const getSectionData = (section) => {
    return editedSections[section.id] || section;
  };

  // Check if section has unsaved changes
  const hasUnsavedChanges = (sectionId) => {
    return !!editedSections[sectionId];
  };

  // Save section changes to API
  const saveSectionChanges = async (sectionId) => {
    const editedData = editedSections[sectionId];
    if (!editedData) return;

    setSavingSection(sectionId);
    try {
      const response = await authFetch(`${API_URL}/api/admin/featured-sections/${sectionId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify(editedData)
      });

      if (response.ok) {
        // Update the main state with saved data
        setFeaturedSections(prev => 
          prev.map(s => s.id === sectionId ? { ...s, ...editedData } : s)
        );
        // Clear edited state for this section
        setEditedSections(prev => {
          const newState = { ...prev };
          delete newState[sectionId];
          return newState;
        });
        toast.success('Secció guardada correctament');
      } else {
        toast.error('Error guardant secció');
      }
    } catch (error) {
      toast.error('Error guardant secció');
    } finally {
      setSavingSection(null);
    }
  };

  // Discard section changes
  const discardSectionChanges = (sectionId) => {
    setEditedSections(prev => {
      const newState = { ...prev };
      delete newState[sectionId];
      return newState;
    });
    toast.info('Canvis descartats');
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#1A4D2E]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-home-content">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-display font-semibold text-[#1A4D2E] flex items-center gap-2">
              <Home className="w-6 h-6" />
              Contingut de la Pàgina Principal
            </h1>
            <p className="text-[#666666] mt-1">
              Edita els textos i seccions destacades de la homepage
            </p>
          </div>
        </div>

        {/* Info banner about images */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-semibold mb-1">Format d'imatges recomanat:</p>
              <ul className="list-disc list-inside space-y-0.5">
                <li><strong>Imatge de benvinguda:</strong> 800×600 px (proporció 4:3), format JPG/WebP, màx. 200KB</li>
                <li><strong>Targetes de seccions:</strong> 800×600 px (proporció 4:3), format JPG/WebP, màx. 150KB</li>
                <li>Puja les imatges a un servei com <a href="https://imgur.com" target="_blank" rel="noopener noreferrer" className="underline">Imgur</a> o utilitza URLs directes</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-[#E5E5E5]">
          <button
            onClick={() => setActiveTab('texts')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'texts' 
                ? 'border-[#1A4D2E] text-[#1A4D2E]' 
                : 'border-transparent text-[#666666] hover:text-[#333333]'
            }`}
          >
            <Type className="w-4 h-4 inline mr-2" />
            Textos
          </button>
          <button
            onClick={() => setActiveTab('sections')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'sections' 
                ? 'border-[#1A4D2E] text-[#1A4D2E]' 
                : 'border-transparent text-[#666666] hover:text-[#333333]'
            }`}
          >
            <Image className="w-4 h-4 inline mr-2" />
            Seccions Destacades ({featuredSections.length})
          </button>
        </div>

        {activeTab === 'texts' && (
          <div className="space-y-6">
            {/* Language selector */}
            <div className="flex gap-2 bg-[#F5F5F5] p-1 rounded-lg w-fit">
              {languages.map((l) => (
                <button
                  key={l.code}
                  onClick={() => setActiveLang(l.code)}
                  className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                    l.code === activeLang 
                      ? 'bg-white text-[#1A4D2E] shadow-sm' 
                      : 'text-[#666666] hover:text-[#333333]'
                  }`}
                >
                  {l.flag} {l.code.toUpperCase()}
                </button>
              ))}
            </div>

            {/* Welcome Section */}
            <div className="bg-white border border-[#E5E5E5] rounded-lg p-6 space-y-4">
              <h3 className="font-semibold text-[#333333]">Secció de Benvinguda</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Pretítol</Label>
                  <Input
                    value={homeContent.welcome_pretitle?.[activeLang] || ''}
                    onChange={(e) => updateTextField('welcome_pretitle', e.target.value)}
                    placeholder="Ex: UGOLF ARAVELL Andorra"
                    data-testid="welcome-pretitle"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Títol principal</Label>
                  <Input
                    value={homeContent.welcome_title?.[activeLang] || ''}
                    onChange={(e) => updateTextField('welcome_title', e.target.value)}
                    placeholder="Ex: Golf als Pirineus"
                    data-testid="welcome-title"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Subtítol / Descripció</Label>
                <RichTextEditor
                  value={homeContent.welcome_subtitle?.[activeLang] || ''}
                  onChange={(value) => updateTextField('welcome_subtitle', value)}
                  placeholder="Descripció de benvinguda..."
                  minHeight={100}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>URL de la imatge (800×600 px recomanat)</Label>
                  <Input
                    value={homeContent.welcome_image || ''}
                    onChange={(e) => setHomeContent(prev => ({ ...prev, welcome_image: e.target.value }))}
                    placeholder="https://..."
                    data-testid="welcome-image"
                  />
                  {homeContent.welcome_image && (
                    <img 
                      src={homeContent.welcome_image} 
                      alt="Preview" 
                      className="mt-2 max-h-32 rounded border"
                    />
                  )}
                </div>
                <div className="space-y-2">
                  <Label>URL del botó CTA (Reserva)</Label>
                  <Input
                    value={homeContent.welcome_cta_link || ''}
                    onChange={(e) => setHomeContent(prev => ({ ...prev, welcome_cta_link: e.target.value }))}
                    placeholder="https://open.teeone.golf/es/aravell"
                    data-testid="welcome-cta"
                  />
                </div>
              </div>
            </div>

            {/* Webcam Section */}
            <div className="bg-white border border-[#E5E5E5] rounded-lg p-6 space-y-4">
              <h3 className="font-semibold text-[#333333]">Secció Webcam / Estat del Camp</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Pretítol</Label>
                  <Input
                    value={homeContent.webcam_pretitle?.[activeLang] || ''}
                    onChange={(e) => updateTextField('webcam_pretitle', e.target.value)}
                    placeholder="Ex: Informació en temps real"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Títol</Label>
                  <Input
                    value={homeContent.webcam_title?.[activeLang] || ''}
                    onChange={(e) => updateTextField('webcam_title', e.target.value)}
                    placeholder="Ex: Estat del camp i accessos"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Subtítol</Label>
                <Input
                  value={homeContent.webcam_subtitle?.[activeLang] || ''}
                  onChange={(e) => updateTextField('webcam_subtitle', e.target.value)}
                  placeholder="Descripció breu..."
                />
              </div>
            </div>

            {/* Featured Section Titles */}
            <div className="bg-white border border-[#E5E5E5] rounded-lg p-6 space-y-4">
              <h3 className="font-semibold text-[#333333]">Secció Experiència Completa (Títols)</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Pretítol</Label>
                  <Input
                    value={homeContent.featured_pretitle?.[activeLang] || ''}
                    onChange={(e) => updateTextField('featured_pretitle', e.target.value)}
                    placeholder="Ex: Descobreix"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Títol</Label>
                  <Input
                    value={homeContent.featured_title?.[activeLang] || ''}
                    onChange={(e) => updateTextField('featured_title', e.target.value)}
                    placeholder="Ex: Experiència Completa"
                  />
                </div>
              </div>
            </div>

            {/* Location Section */}
            <div className="bg-white border border-[#E5E5E5] rounded-lg p-6 space-y-4">
              <h3 className="font-semibold text-[#333333]">Secció Ubicació</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Pretítol</Label>
                  <Input
                    value={homeContent.location_pretitle?.[activeLang] || ''}
                    onChange={(e) => updateTextField('location_pretitle', e.target.value)}
                    placeholder="Ex: On Som"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Títol</Label>
                  <Input
                    value={homeContent.location_title?.[activeLang] || ''}
                    onChange={(e) => updateTextField('location_title', e.target.value)}
                    placeholder="Ex: Al cor dels Pirineus"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Descripció</Label>
                <RichTextEditor
                  value={homeContent.location_description?.[activeLang] || ''}
                  onChange={(value) => updateTextField('location_description', value)}
                  placeholder="Descripció de la ubicació..."
                  minHeight={100}
                />
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-between items-center sticky bottom-16 bg-white py-4 mt-6 border-t border-[#E5E5E5] -mx-6 px-6 shadow-lg rounded-lg z-10">
              <Button
                variant="outline"
                onClick={handleInitContent}
                className="gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Inicialitzar amb valors per defecte
              </Button>
              
              <Button
                onClick={handleSaveHomeContent}
                disabled={saving}
                className="bg-[#1A4D2E] hover:bg-[#153d25] text-white"
                data-testid="save-texts-btn"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Guardar Textos
              </Button>
            </div>
          </div>
        )}

        {activeTab === 'sections' && (
          <div className="space-y-4">
            {/* Language selector for sections */}
            <div className="flex gap-2 bg-[#F5F5F5] p-1 rounded-lg w-fit">
              {languages.map((l) => (
                <button
                  key={l.code}
                  onClick={() => setActiveLang(l.code)}
                  className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                    l.code === activeLang 
                      ? 'bg-white text-[#1A4D2E] shadow-sm' 
                      : 'text-[#666666] hover:text-[#333333]'
                  }`}
                >
                  {l.flag} {l.code.toUpperCase()}
                </button>
              ))}
            </div>

            {/* Featured sections list */}
            {featuredSections.length === 0 ? (
              <div className="bg-[#F9F9F7] border border-dashed border-[#E5E5E5] rounded-lg p-8 text-center">
                <p className="text-[#666666] mb-4">No hi ha seccions destacades</p>
                <Button onClick={handleInitSections} variant="outline" className="gap-2">
                  <RefreshCw className="w-4 h-4" />
                  Inicialitzar seccions per defecte
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {featuredSections.map((section, index) => {
                  const sectionData = getSectionData(section);
                  const hasChanges = hasUnsavedChanges(section.id);
                  const isSaving = savingSection === section.id;
                  
                  return (
                  <div 
                    key={section.id}
                    className={`bg-white border rounded-lg overflow-hidden ${hasChanges ? 'border-orange-400 ring-1 ring-orange-200' : 'border-[#E5E5E5]'}`}
                    data-testid={`section-${section.id}`}
                  >
                    {/* Section header */}
                    <div 
                      className="flex items-center justify-between px-4 py-3 bg-[#F9F9F7] cursor-pointer hover:bg-[#F5F5F5]"
                      onClick={() => toggleSection(section.id)}
                    >
                      <div className="flex items-center gap-3">
                        <GripVertical className="w-4 h-4 text-[#888888]" />
                        <span className="font-medium text-sm text-[#333333]">
                          {sectionData.title?.[activeLang] || sectionData.title?.ca || 'Sense títol'}
                        </span>
                        {hasChanges && (
                          <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded">
                            Canvis pendents
                          </span>
                        )}
                        {sectionData.image && (
                          <img src={sectionData.image} alt="" className="w-10 h-8 object-cover rounded" />
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); deleteFeaturedSection(section.id); }}
                          className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                        {expandedSections[section.id] ? (
                          <ChevronUp className="w-4 h-4 text-[#888888]" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-[#888888]" />
                        )}
                      </div>
                    </div>

                    {/* Section content */}
                    {expandedSections[section.id] && (
                      <div className="p-4 border-t border-[#E5E5E5] space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Títol ({activeLang.toUpperCase()})</Label>
                            <Input
                              value={sectionData.title?.[activeLang] || ''}
                              onChange={(e) => updateSectionLocal(section.id, {
                                title: { ...sectionData.title, [activeLang]: e.target.value }
                              })}
                              placeholder="Títol de la secció"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Enllaç intern</Label>
                            <Input
                              value={sectionData.link || ''}
                              onChange={(e) => updateSectionLocal(section.id, { link: e.target.value })}
                              placeholder="/el-camp"
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label>Descripció ({activeLang.toUpperCase()})</Label>
                          <RichTextEditor
                            value={sectionData.description?.[activeLang] || ''}
                            onChange={(value) => updateSectionLocal(section.id, {
                              description: { ...sectionData.description, [activeLang]: value }
                            })}
                            placeholder="Descripció breu..."
                            minHeight={80}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label>URL de la imatge (800×600 px recomanat)</Label>
                          <Input
                            value={sectionData.image || ''}
                            onChange={(e) => updateSectionLocal(section.id, { image: e.target.value })}
                            placeholder="https://..."
                          />
                          {sectionData.image && (
                            <img 
                              src={sectionData.image} 
                              alt="Preview" 
                              className="mt-2 max-h-24 rounded border"
                            />
                          )}
                        </div>

                        <div className="flex items-center justify-between pt-2 border-t border-[#E5E5E5]">
                          <label className="flex items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              checked={sectionData.is_active !== false}
                              onChange={(e) => updateSectionLocal(section.id, { is_active: e.target.checked })}
                              className="rounded border-gray-300"
                            />
                            Activa (visible a la web)
                          </label>
                          
                          {/* Save / Discard buttons */}
                          <div className="flex items-center gap-2">
                            {hasChanges && (
                              <>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => discardSectionChanges(section.id)}
                                  disabled={isSaving}
                                >
                                  Descartar
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => saveSectionChanges(section.id)}
                                  disabled={isSaving}
                                  className="bg-[#1A4D2E] hover:bg-[#15412A] gap-2"
                                >
                                  {isSaving ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Save className="w-4 h-4" />
                                  )}
                                  Desar secció
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            )}

            {/* Add section button */}
            <div className="flex justify-between items-center pt-4">
              <Button
                variant="outline"
                onClick={addFeaturedSection}
                className="gap-2"
                data-testid="add-section-btn"
              >
                <Plus className="w-4 h-4" />
                Afegir Secció
              </Button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminHomeContent;
